import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, getDocs, addDoc, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';

// Load Tailwind CSS from CDN
const tailwindScript = document.createElement('script');
tailwindScript.src = 'https://cdn.tailwindcss.com';
document.head.appendChild(tailwindScript);

// Pastikan untuk mengganti konfigurasi ini dengan konfigurasi Firebase Anda yang sebenarnya
const firebaseConfig = {
  apiKey: "AIzaSyC4VtiCNGq_byyWxqqnmt_ywCIu4oh_8Es",
  authDomain: "cranelist-4027c.firebaseapp.com",
  projectId: "cranelist-4027c",
  storageBucket: "cranelist-4027c.firebasestorage.app",
  messagingSenderId: "674433432121",
  appId: "1:674433432121:web:81f219113b0934f3b66be8"
};

// Inisialisasi Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// Komponen Notifikasi yang akan muncul di layar
const Notification = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000); // Notifikasi akan hilang setelah 3 detik
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-5 left-1/2 -translate-x-1/2 z-50 p-4 rounded-xl shadow-lg bg-green-500 text-white font-semibold flex items-center space-x-3 transition-all duration-300 animate-slide-in">
      <i className="fa-solid fa-check-circle text-2xl"></i>
      <span>{message}</span>
    </div>
  );
};

// PinPad component untuk otentikasi
const PinPad = ({ onAuthenticate, showNotification, correctPin }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handlePinInput = (value) => {
    if (value === 'clear') {
      setPin('');
      setError('');
    } else if (value === 'backspace') {
      setPin(prevPin => prevPin.slice(0, -1));
      setError('');
    } else if (pin.length < 4) {
      const newPin = pin + value;
      setPin(newPin);
      if (newPin.length === 4) {
        if (newPin === correctPin) {
          onAuthenticate(true);
          showNotification('Login berhasil!');
        } else {
          setError('PIN salah!');
          setPin('');
        }
      }
    }
  };

  const NumpadButton = ({ value }) => (
    <button
      onClick={() => handlePinInput(value)}
      className="w-16 h-16 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-2xl rounded-full transition-colors font-bold"
    >
      {value}
    </button>
  );

  return (
    <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-2xl shadow-inner max-w-sm mx-auto text-center">
      <h3 className="text-xl font-bold mb-2 text-gray-800">Selamat datang, Dea !</h3>
      <p className="mb-4 text-gray-600">Silahkan masukkan pin untuk akses</p>
      <div className="flex space-x-2 mb-4">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className={`w-8 h-8 rounded-full border-2 transition-all ${
              pin.length > index ? 'bg-blue-500 border-blue-500' : 'bg-gray-300 border-gray-400'
            }`}
          ></div>
        ))}
      </div>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
          <NumpadButton key={num} value={num.toString()} />
        ))}
        <button
          onClick={() => handlePinInput('clear')}
          className="w-16 h-16 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-lg rounded-full transition-colors font-semibold"
        >
          Clear
        </button>
        <NumpadButton value="0" />
        <button
          onClick={() => handlePinInput('backspace')}
          className="w-16 h-16 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-xl rounded-full transition-colors font-semibold"
        >
          <i className="fa-solid fa-backspace"></i>
        </button>
      </div>
      <p className="mt-6 text-sm text-gray-500">Apps By : Rzfachri</p>
    </div>
  );
};

// Komponen Modal umum untuk pop-up
const Modal = ({ children, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="relative bg-white p-6 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto w-full max-w-2xl mx-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors"
        >
          <i className="fa-solid fa-times fa-lg"></i>
        </button>
        {children}
      </div>
    </div>
  );
};

// Komponen untuk modal konfirmasi
const ConfirmationModal = ({ message, onConfirm, onCancel }) => {
  return (
    <Modal onClose={onCancel}>
      <div className="text-center p-4">
        <h2 className="text-xl font-bold mb-4">{message}</h2>
        <div className="flex justify-center space-x-4">
          <button
            onClick={onConfirm}
            className="px-6 py-2 rounded-full bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors"
          >
            Ya
          </button>
          <button
            onClick={onCancel}
            className="px-6 py-2 rounded-full bg-gray-300 text-gray-700 font-semibold hover:bg-gray-400 transition-colors"
          >
            Tidak</button>
        </div>
      </div>
    </Modal>
  );
};

// Komponen untuk modal PIN superuser
const SuperuserPinModal = ({ onClose, onPinCorrect, showNotification, correctPin }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handlePinInput = (value) => {
    if (value === 'clear') {
      setPin('');
      setError('');
    } else if (value === 'backspace') {
      setPin(prevPin => prevPin.slice(0, -1));
      setError('');
    } else if (pin.length < 4) {
      const newPin = pin + value;
      setPin(newPin);
      if (newPin.length === 4) {
        if (newPin === correctPin) {
          onPinCorrect();
          showNotification('Akses Superuser diberikan!');
        } else {
          setError('PIN salah!');
          setPin('');
        }
      }
    }
  };

  const NumpadButton = ({ value }) => (
    <button
      onClick={() => handlePinInput(value)}
      className="w-16 h-16 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-2xl rounded-full transition-colors font-bold"
    >
      {value}
    </button>
  );

  return (
    <Modal onClose={onClose}>
      <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-2xl shadow-inner max-w-sm mx-auto text-center">
        <h3 className="text-xl font-bold mb-2 text-red-700">Akses Admin</h3>
        <p className="mb-4 text-gray-600">Masukkan PIN Superuser</p>
        <div className="flex space-x-2 mb-4">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                pin.length > index ? 'bg-red-500 border-red-500' : 'bg-gray-300 border-gray-400'
              }`}
            ></div>
          ))}
        </div>
        {error && <p className="text-red-500 mb-4">{error}</p>}
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
            <NumpadButton key={num} value={num.toString()} />
          ))}
          <button
            onClick={() => handlePinInput('clear')}
            className="w-16 h-16 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-lg rounded-full transition-colors font-semibold"
          >
            Clear
          </button>
          <NumpadButton value="0" />
          <button
            onClick={() => handlePinInput('backspace')}
            className="w-16 h-16 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-xl rounded-full transition-colors font-semibold"
          >
            <i className="fa-solid fa-backspace"></i>
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Komponen untuk mengubah PIN
const PinChangeModal = ({ onClose, onPinChange, correctPin, showNotification }) => {
  const [oldPin, setOldPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (oldPin !== correctPin) {
      setError('PIN lama salah!');
      return;
    }
    if (newPin.length !== 4) {
      setError('PIN baru harus 4 digit!');
      return;
    }
    if (newPin !== confirmNewPin) {
      setError('Konfirmasi PIN baru tidak cocok!');
      return;
    }
    setError('');
    onPinChange(newPin);
    showNotification('PIN berhasil diubah!');
    onClose();
  };

  const handleInputChange = (e, setter) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 4) {
      setter(value);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="bg-gray-50 p-6 rounded-xl shadow-inner">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Ubah PIN</h2>
        <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">PIN Lama</label>
            <input
              type="password"
              value={oldPin}
              onChange={(e) => handleInputChange(e, setOldPin)}
              placeholder="Masukkan PIN lama"
              maxLength={4}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">PIN Baru</label>
            <input
              type="password"
              value={newPin}
              onChange={(e) => handleInputChange(e, setNewPin)}
              placeholder="Masukkan PIN baru"
              maxLength={4}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Konfirmasi PIN Baru</label>
            <input
              type="password"
              value={confirmNewPin}
              onChange={(e) => handleInputChange(e, setConfirmNewPin)}
              placeholder="Konfirmasi PIN baru"
              maxLength={4}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {error && <div className="text-red-500 text-sm text-center">{error}</div>}
          <div className="flex justify-center mt-4">
            <button
              type="submit"
              className="px-8 py-3 text-lg font-bold rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-all duration-300"
            >
              Simpan PIN
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

// Komponen untuk form tambah/edit unit
const AddOrEditUnitForm = ({ unit = null, onSave, onClose, showNotification }) => {
  const isEditing = unit !== null;
  const [formData, setFormData] = useState(
    isEditing ?
      {
        ...unit,
        hargaSewa: unit.hargaSewa.toString(),
        insentifOperatorHelper: unit.insentifOperatorHelper.toString(),
        biayaMobDemob: unit.biayaMobDemob.toString(),
        kapasitas: unit.kapasitas ? unit.kapasitas.toString() : '',
        serialNumber: unit.serialNumber || '',
        masaAktifSIA: unit.masaAktifSIA || '',
        nomorUnitManual: unit.nomorUnit.split('-')[1] || ''
      }
      :
      {
        id: '',
        namaUnit: '',
        merekUnit: '',
        jenisCrane: '',
        nomorUnitManual: '',
        nomorUnit: '',
        tahunUnit: '',
        statusUnit: 'Standby',
        kondisi: 'Standby',
        kapasitas: '',
        serialNumber: '',
        masaAktifSIA: '',
        namaPenyewa: '',
        tanggalMulaiSewa: '',
        tanggalBerakhirSewa: '',
        hargaSewa: '',
        insentifOperatorHelper: '',
        biayaMobDemob: '',
        lokasiKerja: ''
      }
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    const isValid = formData.namaUnit && formData.merekUnit && formData.jenisCrane && formData.nomorUnitManual && formData.tahunUnit;
    const isTenantRequired = formData.statusUnit === 'On duty' || formData.statusUnit === 'On duty dan Maintenance';
    const isTenantFilled = formData.namaPenyewa.trim() !== '';
    setIsFormValid(isValid && (!isTenantRequired || isTenantFilled));
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newFormData = { ...formData, [name]: value };

    if (name === 'jenisCrane' || name === 'nomorUnitManual') {
      const prefixMap = {
        'Reach Stacker': 'RS',
        'HMC': 'HMC',
        'Shore Crane': 'SC',
        'Trailer': 'TR',
        'Forklift': 'FL'
      };
      const prefix = prefixMap[newFormData.jenisCrane];
      if (prefix && newFormData.nomorUnitManual) {
        newFormData.nomorUnit = `${prefix}-${newFormData.nomorUnitManual}`;
      } else {
        newFormData.nomorUnit = '';
      }
    }

    if (name === 'merekUnit' || name === 'nomorUnitManual') {
      const brandPrefixMap = {
        'Kalmar': 'KL',
        'Konecranes': 'KC',
        'Hitachi': 'HI',
        'Liebherr': 'LI',
        'UT + Simojoyo': 'UTS',
        'TCM': 'TCM',
      };
      const prefix = brandPrefixMap[newFormData.merekUnit];
      if (prefix && newFormData.nomorUnitManual) {
        newFormData.nomorUnit = `${prefix}-${newFormData.nomorUnitManual}`;
      } else {
        newFormData.nomorUnit = '';
      }
    }

    if (name === 'statusUnit') {
      const newKondisi = value === 'On duty' ? 'On duty' : value === 'On duty dan Maintenance' ? 'On duty dan Maintenance' : 'Standby';
      newFormData.kondisi = newKondisi;
      if (value === 'Standby') {
        newFormData.namaPenyewa = '';
        newFormData.tanggalMulaiSewa = '';
        newFormData.tanggalBerakhirSewa = '';
        newFormData.hargaSewa = '';
        newFormData.insentifOperatorHelper = '';
        newFormData.biayaMobDemob = '';
      }
    }

    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isFormValid) {
      setFormMessage('');
      const dataToSave = {
        ...formData,
        kapasitas: parseFloat(formData.kapasitas) || 0,
        hargaSewa: parseFloat(formData.hargaSewa) || 0,
        insentifOperatorHelper: parseFloat(formData.insentifOperatorHelper) || 0,
        biayaMobDemob: parseFloat(formData.biayaMobDemob) || 0,
      };
      if (!isEditing) {
        delete dataToSave.id;
      }
      try {
        await onSave(dataToSave);
        showNotification(isEditing ? 'Unit berhasil diperbarui!' : 'Unit berhasil ditambahkan!');
        onClose();
      } catch (error) {
      console.error('Error saving unit:', error);
      setFormMessage('Gagal menyimpan unit. Silakan coba lagi.');
    }
  }
};

const isRentalActive = formData.statusUnit === 'On duty' || formData.statusUnit === 'On duty dan Maintenance';
const isTenantRequiredButEmpty = (formData.statusUnit === 'On duty' || formData.statusUnit === 'On duty dan Maintenance') && formData.namaPenyewa.trim() === '';

const formFields = [
  { label: 'Nama Unit', name: 'namaUnit', type: 'text', placeholder: 'Masukkan nama unit...', required: true },
  { label: 'Merek Unit', name: 'merekUnit', type: 'select', options: ['Kalmar', 'Konecranes', 'Hitachi', 'Liebherr', 'UT + Simojoyo', 'TCM'], required: true },
  { label: 'Jenis Crane', name: 'jenisCrane', type: 'select', options: ['Reach Stacker', 'HMC', 'Shore Crane', 'Trailer', 'Forklift'], required: true },
  { label: 'Nomor Unit (Manual)', name: 'nomorUnitManual', type: 'text', placeholder: 'Ex: 001', required: true },
  { label: 'S/N', name: 'serialNumber', type: 'text', placeholder: 'Masukkan Nomor Seri...' },
  { label: 'Kapasitas (ton)', name: 'kapasitas', type: 'number', placeholder: 'Ex: 45', required: true },
  { label: 'Tahun Unit', name: 'tahunUnit', type: 'number', placeholder: 'Ex: 2022', required: true },
  { label: 'Masa Aktif SIA', name: 'masaAktifSIA', type: 'date', required: false },
  { label: 'Status Unit', name: 'statusUnit', type: 'select', options: ['Standby', 'On duty', 'On duty dan Maintenance'], required: true },
];

const rentalFields = [
  { label: 'Nama Penyewa', name: 'namaPenyewa', type: 'text', placeholder: 'Masukkan nama penyewa...', required: isRentalActive },
  { label: 'Periode Sewa (Mulai)', name: 'tanggalMulaiSewa', type: 'date', required: false },
  { label: 'Periode Sewa (Berakhir)', name: 'tanggalBerakhirSewa', type: 'date', required: false },
  { label: 'Harga Sewa', name: 'hargaSewa', type: 'number', placeholder: 'Masukkan harga sewa...', required: false },
  { label: 'Biaya Insentif Operator dan Helper', name: 'insentifOperatorHelper', type: 'number', placeholder: 'Masukkan biaya insentif...', required: false },
  { label: 'Biaya Mob Demob', name: 'biayaMobDemob', type: 'number', placeholder: 'Masukkan biaya mob demob...', required: false },
  { label: 'Lokasi Kerja', name: 'lokasiKerja', type: 'text', placeholder: 'Masukkan lokasi kerja...', required: false },
];

return (
  <div className="bg-gray-50 p-6 rounded-xl shadow-inner">
    <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">{isEditing ? 'Edit Unit' : 'Tambah Unit Baru'}</h2>
    {formMessage && <div className="text-red-500 text-center mb-4">{formMessage}</div>}
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {formFields.map(field => (
        <div key={field.name} className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">{field.label}</label>
          {field.type === 'select' ? (
            <select
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              required={field.required}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="" disabled>Pilih {field.label.toLowerCase()}</option>
              {field.options.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          ) : (
            <input
              type={field.type}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              placeholder={field.placeholder}
              required={field.required}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          )}
        </div>
      ))}
      <div className="flex flex-col">
        <label className="text-sm font-medium text-gray-700 mb-1">Nomor Unit</label>
        <input
          type="text"
          value={formData.nomorUnit}
          readOnly
          className="p-3 border border-gray-300 rounded-lg bg-gray-200 text-gray-500 cursor-not-allowed"
        />
      </div>
      {isRentalActive && (
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 p-6 border border-dashed border-blue-300 rounded-xl bg-blue-50">
          <h3 className="md:col-span-2 text-xl font-semibold text-blue-800 mb-2">Detail Sewa</h3>
          {isTenantRequiredButEmpty && (
              <div className="md:col-span-2 text-red-500 text-sm italic mb-2">Nama Penyewa harus diisi jika status On Duty atau On Duty dan Maintenance.</div>
          )}
          {rentalFields.map(field => (
            <div key={field.name} className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">{field.label}</label>
              <input
                type={field.type}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                placeholder={field.placeholder}
                required={field.required}
                className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ))}
        </div>
      )}
      <div className="md:col-span-2 flex justify-center mt-4">
        <button
          type="submit"
          disabled={!isFormValid}
          className={`flex items-center space-x-2 px-8 py-4 text-lg font-bold rounded-full transition-all duration-300 transform hover:scale-105 ${
            isFormValid ? 'bg-blue-600 text-white shadow-lg hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <i className="fa-solid fa-save" size={24}></i>
          <span>{isEditing ? 'Simpan Perubahan' : 'Tambah Unit Baru'}</span>
        </button>
      </div>
    </form>
  </div>
);
};

// Komponen UnitList untuk menampilkan daftar unit
const UnitList = ({ units, formatRupiah, onEditUnit, onDeleteUnit, onSetStatus, onSetMaintenance, onCompleteMaintenance, onOpenLocationModal }) => {
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const handleDropdownToggle = (id) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const formatIndonesianDate = (dateString) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('-');
    const monthAbbreviations = [
      'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
      'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'
    ];
    return `${parseInt(day, 10)}/${monthAbbreviations[parseInt(month, 10) - 1]}/${year}`;
  };

  const [expandedUnitId, setExpandedUnitId] = useState(null);

  const handleExpand = (id) => {
    setExpandedUnitId(expandedUnitId === id ? null : id);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {units.length > 0 ? (
        units.map(unit => (
          <div key={unit.id} className="relative pt-8 flex flex-col h-full">
            {/* Status Card di atas */}
            <div className={`absolute top-0 left-0 right-0 p-2 rounded-t-2xl shadow-md flex items-center space-x-2 transform transition-all duration-300 ${
              unit.statusUnit === 'On duty' ? 'bg-green-500' :
              (unit.statusUnit === 'On duty dan Maintenance' || unit.statusUnit === 'Standby - MT') ? 'bg-yellow-500' :
              'bg-gray-500'
            }`}>
              <div className="flex-grow flex items-center space-x-2 text-white text-sm">
                {unit.statusUnit === 'On duty' && (
                  <>
                    <i className="fa-solid fa-gear animate-spin"></i>
                    <i className="fa-solid fa-hard-hat opacity-70 animate-pulse"></i>
                  </>
                )}
                {unit.statusUnit.includes('Maintenance') && (
                  <i className="fa-solid fa-wrench fa-spin-pulse"></i>
                )}
                {unit.statusUnit === 'Standby' && (
                  <i className="fa-solid fa-bed"></i>
                )}
                <span className="font-semibold">{unit.statusUnit}</span>
              </div>
            </div>
            {/* Card Utama di bawah */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 flex flex-col justify-between flex-grow relative mt-4">
              <div className="absolute top-4 right-4 z-10 flex items-center space-x-2">
                <div className="relative">
                  <button
                    className="text-gray-500 hover:text-gray-800 transition-colors p-2"
                    onClick={() => handleDropdownToggle(unit.id)}
                  >
                    <i className="fa-solid fa-ellipsis-v"></i>
                  </button>
                  {openDropdownId === unit.id && (
                    <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg py-1 ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <button
                        onClick={() => { onEditUnit(unit); setOpenDropdownId(null); }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <i className="fa-solid fa-edit mr-2"></i>
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => { onDeleteUnit(unit.id); setOpenDropdownId(null); }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <i className="fa-solid fa-trash-alt mr-2"></i>
                        <span>Hapus</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="pt-2">
                <div className="flex items-start mb-4">
                  <h3 className="text-xl font-bold text-blue-800">{unit.namaUnit}</h3>
                </div>
                <div className="space-y-2 text-gray-700">
                  <p className="flex items-center"><i className="fa-solid fa-truck-monster mr-3 text-gray-500"></i><span className="font-semibold">Jenis :</span> {unit.jenisCrane}</p>
                  <p className="flex items-center"><i className="fa-solid fa-weight-hanging mr-3 text-gray-500"></i><span className="font-semibold">Kapasitas :</span> {unit.kapasitas} ton</p>
                  <p className="flex items-center"><i className="fa-solid fa-tag mr-3 text-gray-500"></i><span className="font-semibold">Merek :</span> {unit.merekUnit}</p>
                  <p className="flex items-center"><i className="fa-solid fa-barcode mr-3 text-gray-500"></i><span className="font-semibold">Nomor :</span> {unit.nomorUnit}</p>
                  <p className="flex items-center"><i className="fa-solid fa-calendar-alt mr-3 text-gray-500"></i><span className="font-semibold">Tahun :</span> {unit.tahunUnit}</p>
                  <p className="flex items-center"><i className="fa-solid fa-key mr-3 text-gray-500"></i><span className="font-semibold">S/N :</span> {unit.serialNumber || '-'}</p>
                  <p className="flex items-center"><i className="fa-solid fa-hourglass mr-3 text-gray-500"></i><span className="font-semibold">Masa Aktif SIA :</span> {unit.masaAktifSIA || '-'}</p>
                </div>
  
                {unit.statusUnit === 'Standby' && (
                  <div className="bg-gray-100 rounded-xl p-4 mt-4 border border-dashed border-gray-300">
                    <h4 className="text-md font-bold text-gray-800 mb-2">Lokasi</h4>
                    <div className="space-y-1 text-gray-700 text-sm">
                      <p>{unit.lokasiKerja || '-'}</p>
                    </div>
                    <button onClick={() => onOpenLocationModal(unit)} className="mt-2 text-blue-500 hover:text-blue-700 text-sm font-semibold">
                      Edit Lokasi
                    </button>
                  </div>
                )}
                {(unit.namaPenyewa || unit.tanggalMulaiSewa || unit.lokasiKerja || unit.hargaSewa > 0 || unit.insentifOperatorHelper > 0) && (unit.statusUnit !== 'Standby') && (
                  <div className="bg-gray-100 rounded-xl p-4 mt-4 border border-dashed border-blue-300 cursor-pointer transition-all duration-300 ease-in-out" onClick={() => handleExpand(unit.id)}>
                    <div className="flex justify-between items-center">
                      <h4 className="text-md font-bold text-gray-800">Penyewa</h4>
                      <i className={`fa-solid ${expandedUnitId === unit.id ? 'fa-chevron-up' : 'fa-chevron-down'} text-gray-500 transition-transform duration-300`}></i>
                    </div>
                    {expandedUnitId === unit.id && (
                      <div className="mt-2 space-y-1 text-gray-700 text-sm animate-fade-in">
                        <p><strong>PT.</strong> {unit.namaPenyewa || '-'}</p>
                        <p><strong>Periode:</strong> {unit.tanggalMulaiSewa && unit.tanggalBerakhirSewa ? `${formatIndonesianDate(unit.tanggalMulaiSewa)} - ${formatIndonesianDate(unit.tanggalBerakhirSewa)}` : '-'}</p>
                        <p><strong>Lokasi:</strong> {unit.lokasiKerja || '-'}</p>
                        <p><strong>Harga:</strong> {unit.hargaSewa > 0 ? formatRupiah(unit.hargaSewa) : '-'}</p>
                        <p><strong>Insentif Operator & Hlp:</strong> {unit.insentifOperatorHelper > 0 ? formatRupiah(unit.insentifOperatorHelper) : '-'}</p>
                        <p><strong>Mob Demob:</strong> {unit.biayaMobDemob > 0 ? formatRupiah(unit.biayaMobDemob) : '-'}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-6 flex flex-col space-y-2">
                {(unit.statusUnit === 'On duty' || unit.statusUnit === 'On duty dan Maintenance') ? (
                  <button
                    onClick={() => onSetStatus(unit, 'Standby')}
                    className="w-full px-4 py-2 text-sm rounded-full text-white font-semibold transition-all duration-300 transform hover:scale-105 bg-red-500 hover:bg-red-600"
                  >
                    <i className="fa-solid fa-stop-circle mr-2"></i>
                    <span>Set Standby</span>
                  </button>
                ) : (
                  <button
                    onClick={() => onSetStatus(unit, 'On duty')}
                    className="w-full px-4 py-2 text-sm rounded-full text-white font-semibold transition-all duration-300 transform hover:scale-105 bg-green-500 hover:bg-green-600"
                  >
                    <i className="fa-solid fa-play-circle mr-2"></i>
                    <span>Set On duty</span>
                  </button>
                )}
                {(unit.statusUnit === 'On duty' || unit.statusUnit === 'Standby') && (
                  <button
                    onClick={() => onSetMaintenance(unit)}
                    className="w-full px-4 py-2 text-sm rounded-full text-white font-semibold transition-all duration-300 transform hover:scale-105 bg-yellow-500 hover:bg-yellow-600"
                  >
                    <i className="fa-solid fa-tools mr-2"></i>
                    <span>Set Maintenance</span>
                  </button>
                )}
                {(unit.statusUnit === 'On duty dan Maintenance' || unit.statusUnit === 'Standby - MT') && (
                  <button
                    onClick={() => onCompleteMaintenance(unit)}
                    className="w-full px-4 py-2 text-sm rounded-full text-white font-semibold transition-all duration-300 transform hover:scale-105 bg-orange-500 hover:bg-orange-600"
                  >
                    <i className="fa-solid fa-hammer mr-2"></i>
                    <span>Selesai Maintenance</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="md:col-span-3 text-center py-8 text-gray-500">
          Tidak ada unit yang ditemukan.
        </div>
      )}
    </div>
  );
};

// Komponen PageContent yang berisi UI utama
const PageContent = ({
  units,
  formatRupiah,
  onEditUnit,
  onDeleteUnit,
  onSetStatus,
  onSetMaintenance,
  onCompleteMaintenance,
  onOpenLocationModal,
  searchTerm,
  setSearchTerm,
  loading,
  errorMessage,
  handleOpenAddModal,
  onOpenPinChangeModal,
  handleSort,
  sortOrder,
  onExportData,
  onImportData,
  onClearData,
  isSuperuserAuthenticated,
  onSuperuserAuth
}) => {
  const filteredUnits = units.filter(unit =>
    Object.values(unit).some(value =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );
  
  const sortedUnits = [...filteredUnits].sort((a, b) => {
    if (sortOrder === 'asc') {
      return a.namaUnit.localeCompare(b.namaUnit);
    } else {
      return b.namaUnit.localeCompare(a.namaUnit);
    }
  });

  return (
    <>
      <div className="absolute top-4 right-4 flex items-center space-x-2">
        <button
          onClick={onSuperuserAuth}
          className="p-2 rounded-full transition-colors shadow-lg"
          title="Superuser Access"
        >
          <i className="fa-solid fa-triangle-exclamation fa-lg text-red-500 hover:text-red-600"></i>
        </button>
        <button
          onClick={onOpenPinChangeModal}
          className="p-2 rounded-full text-gray-500 hover:text-gray-800 transition-colors"
          title="Ubah PIN"
        >
          <i className="fa-solid fa-cog fa-lg"></i>
        </button>
      </div>

      <div className="flex flex-col mb-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">Daftar Unit</h2>
          <span className="text-sm text-gray-500 font-semibold">Total Unit: {units.length}</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-center mt-4 space-y-3 md:space-y-0 md:space-x-2 w-full">
          <div className="relative flex-1 w-full md:w-auto">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <i className="fa-solid fa-search w-5 h-5 text-gray-500"></i>
            </div>
            <input
              type="text"
              placeholder="Cari unit..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex w-full md:w-auto space-x-2">
            <button
              onClick={handleOpenAddModal}
              className="px-6 py-3 rounded-full bg-blue-600 text-white shadow-md transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 flex-grow-[8] w-[80%]"
            >
              <i className="fa-solid fa-plus" size={20}></i>
              <span>Tambah Unit</span>
            </button>
            <button
              onClick={handleSort}
              className="p-3 rounded-full bg-gray-500 text-white shadow-md transition-all duration-300 transform hover:scale-105 flex-grow-[2] w-[20%]"
              title={`Urutkan Nama Unit (${sortOrder === 'asc' ? 'A-Z' : 'Z-A'})`}
            >
              <i className={`fa-solid fa-sort-alpha-${sortOrder}`}></i>
            </button>
          </div>
        </div>
        
        {isSuperuserAuthenticated && (
          <div className="mt-4 flex flex-col md:flex-row md:justify-end space-y-2 md:space-y-0 md:space-x-2 animate-fade-in">
            <button
              onClick={onExportData}
              className="px-4 py-2 rounded-full bg-gray-600 text-white shadow-md hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2 text-sm"
            >
              <i className="fa-solid fa-download"></i>
              <span>Ekspor CSV</span>
            </button>
            <label className="px-4 py-2 rounded-full bg-gray-600 text-white shadow-md hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2 text-sm cursor-pointer">
              <i className="fa-solid fa-upload"></i>
              <span>Impor CSV</span>
              <input type="file" className="hidden" accept=".csv" onChange={onImportData} />
            </label>
            <button
              onClick={onClearData}
              className="px-4 py-2 rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 text-sm"
            >
              <i className="fa-solid fa-fire"></i>
              <span>Hapus Semua Data</span>
            </button>
          </div>
        )}
      </div>
      {loading ? (
        <div className="text-center py-8 text-gray-500">
          Memuat data dari Firestore...
        </div>
      ) : errorMessage ? (
        <div className="text-center py-8 text-red-500">
          {errorMessage}
        </div>
      ) : (
        <UnitList
          units={sortedUnits}
          formatRupiah={formatRupiah}
          onEditUnit={onEditUnit}
          onDeleteUnit={onDeleteUnit}
          onSetStatus={onSetStatus}
          onSetMaintenance={onSetMaintenance}
          onCompleteMaintenance={onCompleteMaintenance}
          onOpenLocationModal={onOpenLocationModal}
        />
      )}
    </>
  );
};

// Komponen Modal Edit Lokasi untuk unit Standby
const EditLocationModal = ({ unit, onClose, onSave, showNotification }) => {
  const [location, setLocation] = useState(unit.lokasiKerja || '');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave({ ...unit, lokasiKerja: location });
      showNotification('Lokasi unit berhasil diperbarui!');
      onClose();
    } catch (error) {
      console.error('Error updating location:', error);
      showNotification('Gagal memperbarui lokasi. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Edit Lokasi Unit</h2>
        <p className="text-sm text-gray-600 mb-4 text-center">Unit: **{unit.namaUnit}**</p>
        <div className="flex flex-col mb-4">
          <label className="text-sm font-medium text-gray-700 mb-1">Lokasi</label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Masukkan lokasi unit..."
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="px-6 py-2 rounded-full text-gray-700 font-semibold bg-gray-200 hover:bg-gray-300 transition-colors">
            Batal
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className={`px-6 py-2 rounded-full text-white font-semibold transition-colors ${
              loading ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Main App component
const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSuperuserAuthenticated, setIsSuperuserAuthenticated] = useState(false);
  const [units, setUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [actionToConfirm, setActionToConfirm] = useState(null);
  const [showPinChangeModal, setShowPinChangeModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showSuperuserPinModal, setShowSuperuserPinModal] = useState(false);
  const [pin, setPin] = useState('2208'); // PIN disimpan di state
  const [showBackToTop, setShowBackToTop] = useState(false);

  const handleShowNotification = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
  };

  const fetchUnits = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const unitsCollection = collection(db, 'units');
      const unitSnapshot = await getDocs(unitsCollection);
      const unitList = unitSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUnits(unitList);
    } catch (error) {
      console.error("Error fetching data:", error);
      setErrorMessage('Gagal mengambil data dari Firestore. Periksa konfigurasi dan koneksi Anda.');
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnits();
    }
  }, [isAuthenticated]);

  // Back to Top button logic
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenAddModal = () => {
    setEditingUnit(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (unit) => {
    setEditingUnit(unit);
    setShowModal(true);
  };
  
  const handleOpenLocationModal = (unit) => {
    setEditingUnit(unit);
    setShowLocationModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowLocationModal(false);
    setEditingUnit(null);
    fetchUnits(); // Refresh data setelah modal ditutup
  };

  const addUnit = async (newUnit) => {
    try {
      await addDoc(collection(db, 'units'), newUnit);
      handleShowNotification('Unit berhasil ditambahkan!');
      handleCloseModal();
    } catch (error) {
      console.error('Error adding unit:', error);
      throw new Error('Gagal menambahkan unit. Silakan coba lagi.');
    }
  };

  const saveUnit = async (updatedUnit) => {
    try {
      await updateDoc(doc(db, 'units', updatedUnit.id), updatedUnit);
      handleShowNotification('Unit berhasil diperbarui!');
      handleCloseModal();
    } catch (error) {
      console.error('Error updating unit:', error);
      throw new Error('Gagal memperbarui unit. Silakan coba lagi.');
    }
  };

  const handleDeleteClick = (id) => {
    setActionToConfirm(() => () => handleDeleteConfirmation(id));
    setShowConfirmModal(true);
  };

  const handleDeleteConfirmation = async (id) => {
    setShowConfirmModal(false);
    try {
      await deleteDoc(doc(db, 'units', id));
      handleShowNotification('Unit berhasil dihapus!');
      fetchUnits();
    } catch (error) {
      console.error('Error deleting unit:', error);
      setErrorMessage('Gagal menghapus unit. Silakan coba lagi.');
    }
  };

  const handleSetStatus = (unit, status) => {
    if (status === 'On duty') {
      handleOpenEditModal(unit);
    } else if (status === 'Standby') {
      setActionToConfirm(() => () => handleSetStandby(unit));
      setShowConfirmModal(true);
    }
  };

  const handleSetStandby = async (unit) => {
    setShowConfirmModal(false);
    const updatedUnit = {
      ...unit,
      statusUnit: 'Standby',
      kondisi: 'Standby',
      namaPenyewa: '',
      tanggalMulaiSewa: '',
      tanggalBerakhirSewa: '',
      hargaSewa: 0,
      insentifOperatorHelper: 0,
      biayaMobDemob: 0
    };
    try {
      const unitRef = doc(db, 'units', updatedUnit.id);
      await updateDoc(unitRef, updatedUnit);
      handleShowNotification('Status unit berhasil diubah ke Standby!');
      fetchUnits();
    } catch (error) {
      console.error('Error setting status to Standby:', error);
      setErrorMessage('Gagal mengubah status unit.');
    }
  };

  const handleSetMaintenance = async (unit) => {
    let newStatus = unit.statusUnit === 'Standby' ? 'Standby - MT' : 'On duty dan Maintenance';
    const updatedUnit = { ...unit, statusUnit: newStatus, kondisi: newStatus };

    try {
      const unitRef = doc(db, 'units', updatedUnit.id);
      await updateDoc(unitRef, updatedUnit);
      handleShowNotification('Status unit berhasil diubah ke Maintenance!');
      fetchUnits();
    } catch (error) {
      console.error('Error setting maintenance status:', error);
      setErrorMessage('Gagal mengatur status pemeliharaan.');
    }
  };

  const handleCompleteMaintenance = async (unit) => {
    let newStatus = unit.statusUnit === 'Standby - MT' ? 'Standby' : 'On duty';
    const updatedUnit = { ...unit, statusUnit: newStatus, kondisi: newStatus };

    try {
      const unitRef = doc(db, 'units', updatedUnit.id);
      await updateDoc(unitRef, updatedUnit);
      handleShowNotification('Status pemeliharaan selesai!');
      fetchUnits();
    } catch (error) {
      console.error('Error completing maintenance status:', error);
      setErrorMessage('Gagal menyelesaikan status pemeliharaan.');
    }
  };

  const handleSort = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleExportData = async () => {
    try {
      const unitsCollection = collection(db, 'units');
      const querySnapshot = await getDocs(unitsCollection);
      const unitsData = querySnapshot.docs.map(doc => doc.data());
      
      if (unitsData.length === 0) {
        handleShowNotification('Tidak ada data untuk diekspor!');
        return;
      }

      // Normalisasi data untuk memastikan semua kolom "kapasitas" disatukan
      const normalizedUnits = unitsData.map(unit => {
          const normalizedUnit = { ...unit };
          if (normalizedUnit.kapasitasUnit !== undefined) {
              normalizedUnit.kapasitas = normalizedUnit.kapasitasUnit;
              delete normalizedUnit.kapasitasUnit;
          }
          return normalizedUnit;
      });

      const allKeys = new Set();
      normalizedUnits.forEach(unit => {
        Object.keys(unit).forEach(key => allKeys.add(key));
      });
      const headers = Array.from(allKeys);

      const csvContent = [
        headers.join(','),
        ...normalizedUnits.map(row => headers.map(header => {
          const value = row[header];
          const formattedValue = value !== undefined && value !== null ? value.toString() : '';
          return formattedValue.includes(',') ? `"${formattedValue}"` : formattedValue;
        }).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'data_unit_lct_crane.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      handleShowNotification('Data berhasil diekspor ke CSV!');
    } catch (error) {
      console.error("Error exporting data:", error);
      setErrorMessage('Gagal mengekspor data.');
    }
  };

  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csvText = e.target.result;
          const lines = csvText.split('\n');
          const headers = lines[0].split(',').map(header => header.trim());
          const importedUnits = [];
          
          for (let i = 1; i < lines.length; i++) {
            const currentLine = lines[i].trim();
            if (currentLine === '') continue;
            const values = currentLine.match(/(?:\"[^\"]*\"|[^,])+/g);
            
            if (!values || values.length !== headers.length) {
              console.warn(`Skipping malformed row: ${currentLine}`);
              continue;
            }
            
            const unit = {};
            for (let j = 0; j < headers.length; j++) {
              let value = values[j].replace(/^"|"$/g, '').trim();
              const header = headers[j];
              
              if (['kapasitas', 'hargaSewa', 'insentifOperatorHelper', 'biayaMobDemob'].includes(header)) {
                unit[header] = parseFloat(value) || 0;
              } else if (header === 'tahunUnit') {
                unit[header] = parseInt(value, 10) || 0;
              } else {
                unit[header] = value;
              }
            }
            importedUnits.push(unit);
          }

          if (importedUnits.length > 0) {
            for (const unit of importedUnits) {
              const { id, ...unitWithoutId } = unit;
              await addDoc(collection(db, 'units'), unitWithoutId);
            }
            handleShowNotification(`Berhasil mengimpor ${importedUnits.length} unit!`);
            fetchUnits();
          } else {
            handleShowNotification('File CSV tidak berisi data unit yang valid.');
          }
        } catch (error) {
          console.error("Error importing data:", error);
          handleShowNotification('Gagal mengimpor data. Pastikan format file benar.');
        }
      };
      reader.readAsText(file);
    } else {
      handleShowNotification('Pilih file CSV yang valid.');
    }
  };

  const handleClearDataClick = () => {
    setActionToConfirm(() => () => handleClearData());
    setShowConfirmModal(true);
  };

  const handleClearData = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    setErrorMessage('');
    try {
      const unitsCollection = collection(db, 'units');
      const querySnapshot = await getDocs(unitsCollection);
      const batch = writeBatch(db);

      querySnapshot.docs.forEach((unitDoc) => {
        batch.delete(unitDoc.ref);
      });

      await batch.commit();
      handleShowNotification('Semua data unit berhasil dihapus!');
      setUnits([]);
      setIsSuperuserAuthenticated(false); // Log out superuser after clearing data
    } catch (error) {
      console.error("Error clearing data:", error);
      setErrorMessage('Gagal menghapus semua data. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuperuserAuth = () => {
    setShowSuperuserPinModal(true);
  };
  
  const formatRupiah = (number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(number);
  };
  
  return (
    <>
      <style>
        {`
          @keyframes slide-in {
            0% {
              transform: translateX(-50%) translateY(-100%);
              opacity: 0;
            }
            50% {
              transform: translateX(-50%) translateY(0);
              opacity: 1;
            }
            100% {
              transform: translateX(-50%) translateY(0);
              opacity: 1;
            }
          }
          .animate-slide-in {
            animation: slide-in 0.5s ease-out forwards;
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
          .animate-pulse {
            animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
          @keyframes wrench-spin {
            0%, 100% {
              transform: rotate(0deg);
            }
            25% {
              transform: rotate(20deg);
            }
            50% {
              transform: rotate(-20deg);
            }
            75% {
              transform: rotate(20deg);
            }
          }
          .fa-spin-pulse {
            animation: wrench-spin 1s ease-in-out infinite;
          }
          @keyframes fade-in {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fade-in {
            animation: fade-in 0.3s ease-out;
          }
        `}
      </style>
      {/* Load Font Awesome */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      <div className="min-h-screen bg-gray-100 p-4 font-sans flex items-center justify-center">
        <div id="main-content" className="bg-white p-6 md:p-8 rounded-2xl shadow-xl w-full max-w-7xl">
          <div className="flex flex-col items-center justify-center mb-6">
            <img src="https://res.cloudinary.com/dvlff34qz/image/upload/v1755555083/css/Artboard_1_4x.png" alt="Lemo Crane Logo" className="h-16 mb-2" />
            <h1 className="text-3xl font-extrabold text-center text-blue-800">Manajemen Unit Port Div Lemo Crane</h1>
          </div>

          {!isAuthenticated ? (
            <PinPad onAuthenticate={setIsAuthenticated} showNotification={handleShowNotification} correctPin={pin} />
          ) : (
            <PageContent
              units={units}
              formatRupiah={formatRupiah}
              onEditUnit={handleOpenEditModal}
              onDeleteUnit={handleDeleteClick}
              onSetStatus={handleSetStatus}
              onSetMaintenance={handleSetMaintenance}
              onCompleteMaintenance={handleCompleteMaintenance}
              onOpenLocationModal={handleOpenLocationModal}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              loading={loading}
              errorMessage={errorMessage}
              handleOpenAddModal={handleOpenAddModal}
              onOpenPinChangeModal={() => setShowPinChangeModal(true)}
              handleSort={handleSort}
              sortOrder={sortOrder}
              onExportData={handleExportData}
              onImportData={handleImportData}
              onClearData={handleClearDataClick}
              isSuperuserAuthenticated={isSuperuserAuthenticated}
              onSuperuserAuth={handleSuperuserAuth}
            />
          )}
        </div>
      </div>
      
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 p-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors duration-300 transform hover:scale-110"
          aria-label="Kembali ke Atas"
        >
          <i className="fa-solid fa-arrow-up"></i>
        </button>
      )}

      {showModal && (
        <Modal onClose={handleCloseModal}>
          <AddOrEditUnitForm
            unit={editingUnit}
            onSave={editingUnit ? saveUnit : addUnit}
            onClose={handleCloseModal}
            showNotification={handleShowNotification}
          />
        </Modal>
      )}
      {showLocationModal && (
        <EditLocationModal
          unit={editingUnit}
          onClose={() => handleCloseModal()}
          onSave={saveUnit}
          showNotification={handleShowNotification}
        />
      )}
      {showConfirmModal && (
        <ConfirmationModal
          message="Apakah Anda yakin ingin menghapus unit ini?"
          onConfirm={actionToConfirm}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
      {showPinChangeModal && (
        <PinChangeModal
          onClose={() => setShowPinChangeModal(false)}
          onPinChange={setPin}
          correctPin={pin}
          showNotification={handleShowNotification}
        />
      )}
      {showSuperuserPinModal && (
        <SuperuserPinModal
          onClose={() => setShowSuperuserPinModal(false)}
          onPinCorrect={() => {
            setIsSuperuserAuthenticated(true);
            setShowSuperuserPinModal(false);
          }}
          showNotification={handleShowNotification}
          correctPin="2257"
        />
      )}
      {showNotification && (
        <Notification message={notificationMessage} onClose={() => setShowNotification(false)} />
      )}
    </>
  );
};

export default App;
